import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";

import "./SimAppPrint.css";

import userPhoto from "./assets/img/user_photo.jpg"; // fallback
import nidImage from "./assets/img/nid_photo.jpg";   // fallback

const params = new URLSearchParams(window.location.search);
const isEmail = params.get('source') === 'email';

const API_BASE_URL = isEmail
  ? 'http://127.0.0.1:5000'          // Puppeteer on server → localhost
  : 'http://180.211.162.102:5000'; 

function SimAppPrint() {
  const { id } = useParams();
  const [appData, setAppData] = useState(null);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    inst_name: "",
    inst_code: "",
    inst_eiin: ""
  });

  useEffect(() => {
    const getInstituteData = () => {
      // FIRST: Check URL parameters (for email PDF generation and direct links)
      const urlParams = new URLSearchParams(window.location.search);
      const urlInstName = urlParams.get('inst_name');
      const urlInstCode = urlParams.get('inst_code');
      const urlInstEiin = urlParams.get('inst_eiin');
      const source = urlParams.get('source');
      
      console.log("URL Parameters:", {
        urlInstName,
        urlInstCode, 
        urlInstEiin,
        source
      });
      
      // If URL has parameters, use them (highest priority)
      if (urlInstName || urlInstCode || urlInstEiin) {
        console.log("Using institute data from URL parameters");
        setFormData({
          inst_name: decodeURIComponent(urlInstName || ''),
          inst_code: decodeURIComponent(urlInstCode || ''),
          inst_eiin: decodeURIComponent(urlInstEiin || '')
        });
        return;
      }
      
      // SECOND: Check if admin is viewing another institute temporarily
      const isAdminViewingTemp = localStorage.getItem("isAdminViewingTemp") === "true";
      
      if (isAdminViewingTemp) {
        // Admin is viewing another institute - use temporary data
        const tempInstituteData = JSON.parse(localStorage.getItem("tempInstituteData"));
        if (tempInstituteData) {
          console.log("Using temporary institute data for print:", tempInstituteData.INST_NAME);
          setFormData({
            inst_name: tempInstituteData.INST_NAME || "",
            inst_code: tempInstituteData.INST_ID || "",
            inst_eiin: tempInstituteData.EIIN || ""
          });
          return;
        }
      }
      
      // THIRD: Regular case: use instituteData from localStorage
      const instituteData = JSON.parse(localStorage.getItem("instituteData"));
      if (instituteData) {
        console.log("Using regular institute data for print:", instituteData.INST_NAME);
        setFormData({
          inst_name: instituteData.INST_NAME || "",
          inst_code: instituteData.INST_ID || "",
          inst_eiin: instituteData.EIIN || ""
        });
      } else {
        console.log("No institute data found anywhere");
        setFormData({
          inst_name: "",
          inst_code: "",
          inst_eiin: ""
        });
      }
    };
    
    getInstituteData();
  }, []);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/application/${id}`)
      .then((res) => res.json())
      .then((data) => setAppData(data))
      .catch((err) => console.error("Error loading data:", err));
  }, [id]);

  // Handle logout for print page
  const handleLogout = () => {
    // Clear all authentication data
    localStorage.removeItem("auth");
    localStorage.removeItem("instituteData");
    localStorage.removeItem("currentView");
    localStorage.removeItem("tempInstituteData");
    localStorage.removeItem("isAdminViewingTemp");
    localStorage.removeItem("originalInstituteData");
    
    // Navigate to login page
    navigate("/");
  };

  // Handle back button (not logout)
  const handleBack = () => {
    const auth = JSON.parse(localStorage.getItem("auth"));
    
    if (auth?.isAdmin) {
      // If admin was viewing temporary institute, restore original data
      if (localStorage.getItem("isAdminViewingTemp") === "true") {
        localStorage.removeItem("tempInstituteData");
        localStorage.removeItem("isAdminViewingTemp");
      }
      navigate("/ViewSentApplication");
    } else if (auth?.role === "institute") {
      navigate("/simApp");
    } else {
      navigate("/");
    }
  };

  if (!appData) return <p>Loading...</p>;

  return (
    <>
      {/* Combined Banner with Buttons */}
      <div className="top-banner-with-buttons no-print">
        <div className="banner-message">
          <span className="banner-icon">📢</span>
          আবেদনকারীকে আবেদনপত্র, NID ও জিডি (প্রযোজ্যক্ষেত্রে) নিয়ে নিকটস্থ গ্রামীণফোন সেন্টারে যোগাযোগ করার জন্য অনুরোধ করা হলো।
          <span className="banner-icon">📢</span>
        </div>
        
        <div className="banner-buttons">
          <button className="banner-btn back-btn" onClick={handleBack}>
            ← ফিরে যান
          </button>
          <button className="banner-btn print-btn" onClick={() => window.print()}>
            🖨️ প্রিন্ট
          </button>
          <button className="banner-btn logout-btn" onClick={handleLogout}>
            🔒 লগ আউট
          </button>
        </div>
      </div>

      <div className="application-wrapper">
        {/* Header center aligned */}
        <div className="top-header">
          <p>প্রতিষ্ঠানের নামঃ {formData.inst_name || "N/A"}</p>
          <p>প্রতিষ্ঠানের কোড: {formData.inst_code || "N/A"}</p>
          <p>প্রতিষ্ঠানের EIIN: {formData.inst_eiin || "N/A"}</p>
        </div>

        {/* Applicant Photo (left) */}
        <div className="photo-wrapper">
          <img
            src={appData.image ? `${API_BASE_URL}/uploads/${appData.image}` : userPhoto}
            alt="Applicant"
            className="profile-photo"
            onError={(e) => {
              e.target.src = userPhoto;
            }}
          />
        </div>

        {/* বরাবর Section (left) */}
        <div className="meta">
          <p>তারিখঃ {
            appData?.apply_date ? 
              // Add 6 hours to get Bangladesh time, then format
              new Date(new Date(appData.apply_date).getTime())
                .toLocaleDateString('en-GB')
            : 
            new Date().toLocaleDateString('en-GB')
          }</p>
          <p>বরাবর</p>
          <p>একাউন্ট ম্যানেজার</p>
          <p>গ্রামীণ ফোন লিমিটেড</p>
        </div>
        <div style={{ height: "12px" }}></div>

        {/* Subject (left) */}
        <p className="subject">
          বিষয়ঃ &nbsp;<strong> 
            {appData.application_subject === "new_sim" && "নতুন ইআইআইএন সিম উত্তোলনের জন্য আবেদন"}
            {appData.application_subject === "eiin_sim_replacement" && "পুরাতন ইআইআইএন সিম রিপ্লেসমেন্টের জন্য আবেদন"}
            {appData.application_subject === "lost_sim_reissue" && "হারানো সিম উত্তোলনের জন্য আবেদন"}
            {!appData.application_subject && "আবেদনের বিষয় নির্বাচন করুন"}
          </strong>
        </p>

        <div style={{ height: "12px" }}></div>

        {/* Body Paragraphs (left) */}
        <div className="paragraph">
          <p>
            আমি {appData.applicant_name}, {appData.designation}, {formData.inst_name} এর একজন প্রতিনিধি। 
            {appData.application_subject === "new_sim" && (
              <>বাংলাদেশ কারিগরি শিক্ষা বোর্ড হতে প্রদত্ত EIIN/Institute_Code ভিত্তিক আমাদের প্রতিষ্ঠানের সিমটি এখনো উত্তোলন করা হয়নি।</>
            )}
            {appData.application_subject === "eiin_sim_replacement" && (
              <>বাংলাদেশ কারিগরি শিক্ষা বোর্ড হতে প্রদত্ত EIIN/Institute_Code ভিত্তিক আমাদের প্রতিষ্ঠানের সিমটি কাজ করছে না। যার মোবাইল নম্বর হলো {appData.contact_number}।</>
            )}
            {appData.application_subject === "lost_sim_reissue" && (
              <>বাংলাদেশ কারিগরি শিক্ষা বোর্ড হতে প্রদত্ত EIIN/Institute_Code ভিত্তিক আমাদের প্রতিষ্ঠানের সিমটি হারিয়ে গেছে। যার মোবাইল নম্বর হলো {appData.contact_number}। থানায় জিডি করা হয়েছে যার কপি সংযুক্ত করা হল।</>
            )}
          </p>
          <p>
            অতএব, আমাকে 
            {appData.application_subject === "new_sim" && (
              <> ({appData.applicant_name}, {appData.designation}) EIIN/Institute_Code ভিত্তিক একটি নতুন সিম দেওয়ার জন্য অনুরোধ জানানো যাচ্ছে।</>
            )}
            {(appData.application_subject === "eiin_sim_replacement" || appData.application_subject === "lost_sim_reissue") && (
              <> {appData.applicant_name}, {appData.designation} উল্লিখিত মোবাইল নাম্বারের একটি নতুন সিম দেওয়ার জন্য অনুরোধ জানানো যাচ্ছে।</>
            )}
            ধন্যবাদান্তে,
          </p>
        </div>

        {/* Signature Section (left) */}
        <div className="signature">
          <p>আবেদনকারীর স্বাক্ষর:</p>
          <div className="signature-block">
            <p>{appData.applicant_name}</p>
            <p>{appData.designation}</p>
            <p>মোবাইলঃ {appData.mobile_no}</p>
            <p>জাতীয় পরিচয়পত্র নংঃ {appData.nid_no}</p>
          </div>
        </div>

        {/* NID + QR + Seal (bottom aligned) */}
        <div className="nid-section">
          <div className="nid-qr-wrap">
            {appData && (
              <QRCodeCanvas
                value={JSON.stringify(appData.contact_number)}
                size={90}
                level="H"
                includeMargin={true}
              />
            )}
            <img 
              src={appData.nid_image ? `${API_BASE_URL}/uploads/${appData.nid_image}` : nidImage} 
              alt="NID" 
              className="nid-image"
              onError={(e) => {
                e.target.src = nidImage;
              }}
            />
          </div>
          <div className="seal-sign">
            <p>প্রতিষ্ঠান প্রধানের স্বাক্ষর<br></br> ও সীল</p>
          </div>
        </div>

        {/* Footer note (left) */}
        <p className="footer-note">
          বিঃ দ্রঃ  সিম সংগ্রহের সময় আবেদনকারীর মূল এনআইডি সঙ্গে আনতে হবে।
        </p>

        {/* Footer */}
        <footer className="footer">
          <div className="footer-left">
            {appData.gd_image &&
              appData.gd_image.toLowerCase().endsWith(".pdf") && (
                <a
                  href={`${API_BASE_URL}/uploads/${appData.gd_image}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "#000000",
                    textDecoration: "underline",
                    fontWeight: "normal",
                    cursor: "pointer",
                  }}
                >
                  সংযুক্তি: জিডি কপি
                </a>
              )}
          </div>
        </footer>

      </div>
    </>
  );
}

export default SimAppPrint;